/**
 * CompareTab - Feature comparison matrix between products
 * Displays feature-by-feature comparison with competitor
 */

import { memo } from 'react';
import { useCompetitiveMatrix } from '../../../hooks/useCompanies';
import type { FeatureComparison } from '../../../types';
import { featureStatusStyles } from './constants';

interface CompareTabProps {
  productId: string;
  productName: string;
  competitorId: string;
  competitorName: string;
}

function CompareTabComponent({
  productId,
  productName,
  competitorId,
  competitorName: _competitorName, // Used as fallback if API doesn't return name
}: CompareTabProps) {
  void _competitorName; // Silence unused warning - used as fallback
  const { data: matrixData, isLoading, error } = useCompetitiveMatrix(productId, competitorId);

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/2" />
        <div className="h-64 bg-gray-200 rounded" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-gray-500">
        Unable to load feature comparison. This comparison may not be available yet.
      </div>
    );
  }

  if (!matrixData) {
    return (
      <div className="text-center py-8 text-gray-500">
        Select a competitor from the Competitors tab to see a feature comparison.
      </div>
    );
  }

  const featureAreas = Object.entries(matrixData.feature_areas);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {productName} vs {matrixData.competitor_name}
        </h3>
        <span className="text-xs text-gray-500">
          Updated: {new Date(matrixData.last_updated).toLocaleDateString()}
        </span>
      </div>

      {featureAreas.map(([areaName, features]) => (
        <div key={areaName} className="rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <h4 className="font-medium text-gray-900">{areaName}</h4>
          </div>
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Feature
                </th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  {productName}
                </th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  {matrixData.competitor_name}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {features.map((feature: FeatureComparison, idx: number) => (
                <tr key={idx}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{feature.feature_name}</div>
                    {feature.notes && (
                      <div className="text-xs text-gray-500 mt-1">{feature.notes}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        featureStatusStyles[feature.our_status].bg
                      } ${featureStatusStyles[feature.our_status].text}`}
                    >
                      {featureStatusStyles[feature.our_status].label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        featureStatusStyles[feature.competitor_status].bg
                      } ${featureStatusStyles[feature.competitor_status].text}`}
                    >
                      {featureStatusStyles[feature.competitor_status].label}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {featureAreas.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No feature comparison data available for this competitor.
        </div>
      )}
    </div>
  );
}

export const CompareTab = memo(CompareTabComponent);
